import React, { useEffect, useState } from "react";
import styles from "./style.module.css";
import { useTranslation } from "react-i18next";
import Background from "../../images/destination/phiphi.webp"; // Fallback background
import BrandLogo from "../BrandLogo";
const { getCurrentBrand } = require('../../config/brands');

// Helper function to generate srcset attribute string
// Assumes image files exist with suffixes like -640w, -1280w, -1920w
// Example: if basePath is 'path/to/image.webp', it expects 'path/to/image-640w.webp', etc.
// Adjust the logic and sizes based on your actual available image assets.
const generateSrcSet = (basePath) => {
  if (!basePath || typeof basePath !== 'string') return '';
  const parts = basePath.split('.');
  if (parts.length < 2) return ''; // Ensure there's an extension
  const ext = parts.pop();
  const base = parts.join('.');
  // Define the sizes you have available
  const widths = [640, 1280, 1920]; 
  const srcSetEntries = widths.map(w => `${base}-${w}w.${ext} ${w}w`);
  // Add the original image path as the largest option or a fallback
  // srcSetEntries.push(`${basePath} 2560w`); // Example: Assuming original is 2560px wide
  return srcSetEntries.join(', ');
};

const SearchSection = (props) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'th' ? 'th_TH' : 'en_US';
  const [formLoaded, setFormLoaded] = useState(false);
  // Get the current brand information
  const brand = getCurrentBrand();

  // Use background image from brand config or fallback
  const backgroundImage = brand.assets?.background || Background;

  // Generate srcset and sizes attributes
  const bgSrcSet = generateSrcSet(backgroundImage);
  // Define sizes attribute: tells the browser the image width at different viewport widths
  // Adjust these based on your layout and breakpoints
  const bgSizes = "(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 100vw";

  useEffect(() => {
    // Reinitialize busx search form
    const script = document.createElement('script');
    script.src = "https://cdn.busx.com/gds/resources/assets/js/busx-search-form-react.min.js";
    script.type = "module";

    // Set formLoaded to true when the form is fully initialized
    script.onload = () => {
      // Give a small delay to ensure the form is fully rendered
      setTimeout(() => setFormLoaded(true), 300);
    };

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div
      className={styles.searchSection}
      style={{
        position: "relative", // Keep relative positioning for the container
        width: "100%",
        margin: "0",
        padding: "20px 0 80px 0",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* 
        LCP Optimization Recommendations:
        1. Preload: Consider adding a <link rel="preload"> tag for the appropriate 'backgroundImage' 
           in your main HTML <head>. This helps the browser discover and start downloading the LCP image sooner.
           Example (needs dynamic injection based on brand): 
           <link rel="preload" fetchpriority="high" as="image" href="/path/to/brand-background-1920w.webp" type="image/webp" imagesrcset="..." imagesizes="...">
        2. Image Format/Compression: Use modern formats like WebP or AVIF for better compression. 
           Ensure images are adequately compressed. Use the <picture> element for format fallbacks if needed.
        3. CDN: Serve images from a CDN.
        4. Create Image Sizes: Ensure you have generated the different image sizes referenced in 'generateSrcSet' 
           (e.g., background-640w.webp, background-1280w.webp, background-1920w.webp).
      */}
      <img 
        src={backgroundImage} // Fallback src
        srcSet={bgSrcSet}     // Provide different sizes
        sizes={bgSizes}       // Define image size relative to viewport
        alt="" // Decorative background image
        loading="eager" // Load eagerly as it's critical for LCP
        fetchpriority="high" // Prioritize download
        className={styles.backgroundImage} // Apply styles for positioning
      />
      <div className="container" style={{ position: "relative", zIndex: 5 }}>
        <div className="row">
          {/* SEO-friendly H1 heading */}
          <div className="col-12 text-center">            
            <h1 style={{ 
              fontSize: "1px", 
              margin: 0, 
              padding: 0, 
              color: "transparent",
              position: "absolute",
              overflow: "hidden",
              height: "1px",
              width: "1px"
            }}>
              {brand.mainHeading || `${brand.name} - ${t('seo.mainHeading', 'Online Bus Ticket Booking')}`}
            </h1>
          </div>
          {/* Logo for small screens */}
          <div className="d-lg-none text-center mb-3">
            <BrandLogo
              alt="Logo"
              style={{
                maxWidth: "150px",
                width: "auto",
                display: "block",
                margin: "0 auto",
              }}
              loading="eager"
              fetchpriority="high"
            />
          </div>
          <div className="col-lg-12">
            <div className={`${styles.searchWrapper}`}>
              <h2
                className={styles.searchHeader}
                style={{ color: "#fff", marginBottom: "20px" }}
              >
                {t('search.header')}
              </h2>
              <div
                className={`${styles.searchForm} ${formLoaded ? 'loaded' : 'loading'}`}
                style={{
                  opacity: formLoaded ? 1 : 0.3,
                  transition: 'opacity 0.3s ease',
                  position: 'relative',
                  zIndex: 10, // Ensure search form is above other elements
                  minHeight: '541px', // Add this: Reserve space to prevent layout shift. Adjust value as needed.
                }}
              >
                <div
                  id="busx-search-form"
                  data-url="https://booking.ezseat.asia"
                  data-appkey="26b0df8f436dd61564d1ade0f1b24250"
                  data-locale={locale}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchSection;